import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FlaskConical, History } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { formatDateTime } from '../../utils/Utils';
import { startSsoTest } from '../../services/ssoService';
import SsoEventsDrawer from './SsoEventsDrawer';
import {
  useSsoConnections, getConnectionSchema, toFormValues, toRequest, Badge, DOMAIN_STATUS_COLORS, ENFORCEMENT, PROTOCOLS,
} from './config';

// Kurumsal Giriş (SSO) — yalnız enterpriseAdmin (rota + menü + sunucu kapısı; spec §10.1). Liste EmployeesPage kalıbında
// tek kaydırmalı DataTable; ekleme/düzenleme GenericFormDrawer (modal YOK). Holdingden miras gelen bağlantı salt okunur satırdır.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export default function SsoSettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, accessibleTenants } = useAuth();
  const isHolding = (accessibleTenants || []).find((x) => x.id === user?.tenantId)?.tenantType === 1;
  const { rows, fetching, failed, loadOne, save, remove } = useSsoConnections();
  const [drawer, setDrawer] = useState({ open: false, connection: null });
  const [eventsFor, setEventsFor] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const openCreate = useCallback(() => setDrawer({ open: true, connection: null }), []);
  const openEdit = useCallback(async (row) => {
    const full = await loadOne(row.id);
    if (full) setDrawer({ open: true, connection: full });
  }, [loadOne]);

  const handleSave = useCallback(async (formData) => {
    const ok = await save(drawer.connection?.id || null, toRequest(formData));
    if (ok) setDrawer({ open: false, connection: null });
    return ok;
  }, [drawer.connection, save]);

  // Test açılır pencerede (sekmede) koşar; pencere tıklamayla AYNI anda açılır ki engelleyiciye takılmasın.
  const handleTest = useCallback((row) => {
    const win = window.open('about:blank', '_blank');
    startSsoTest(row.id)
      .then((res) => {
        const url = res?.data?.data?.url;
        if (!url) { win?.close(); return; }
        if (win) win.location.href = url; else window.location.assign(url);
      })
      .catch(() => win?.close());
  }, []);

  const statusLabels = useMemo(() => ({ 1: t('sso.domainStatus.1'), 2: t('sso.domainStatus.2'), 3: t('sso.domainStatus.3') }), [t]);

  const actions = useMemo(() => [
    { type: 'edit', label: t('sso.actions.edit'), onClick: openEdit, condition: (r) => !r.isInherited },
    { type: 'test', label: t('sso.actions.test'), icon: FlaskConical, onClick: handleTest, condition: (r) => !r.isInherited },
    { type: 'events', label: t('sso.actions.events'), icon: History, onClick: (r) => setEventsFor(r), condition: (r) => !r.isInherited },
    { type: 'divider' },
    { type: 'delete', label: t('sso.actions.delete'), onClick: (r) => setDeleting(r), condition: (r) => !r.isInherited },
  ], [t, openEdit, handleTest]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { display: 'excluded', filter: false, sort: false } },
    {
      name: 'displayName', label: t('sso.columns.name'),
      options: {
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          return (
            <span>
              {v}
              {row?.isInherited && <Badge $bg="#EEF4FF" $color="#3538CD" style={{ marginLeft: 8 }}>{t('sso.inherited')}</Badge>}
            </span>
          );
        },
      },
    },
    { name: 'protocol', label: t('sso.columns.protocol'), options: { customBodyRender: (v) => (v === PROTOCOLS.SAML ? 'SAML 2.0' : 'OIDC') } },
    { name: 'providerKind', label: t('sso.columns.provider'), options: { customBodyRender: (v) => t(`sso.providers.${v}`) } },
    {
      name: 'domains', label: t('sso.columns.domains'),
      options: {
        sort: false,
        customBodyRender: (domains) => (domains || []).map((d) => (
          <Badge key={d.id} $bg={DOMAIN_STATUS_COLORS[d.status].bg} $color={DOMAIN_STATUS_COLORS[d.status].color}>
            {d.domain} · {statusLabels[d.status]}
          </Badge>
        )),
      },
    },
    { name: 'enforcement', label: t('sso.columns.enforcement'), options: { customBodyRender: (v) => (v === ENFORCEMENT.REQUIRED ? t('sso.enforcement.required') : t('sso.enforcement.optional')) } },
    {
      name: 'lastTestedAt', label: t('sso.columns.lastTest'),
      options: {
        customBodyRender: (v, meta) => {
          if (!v) return t('sso.notTested');
          const ok = rows[meta.rowIndex]?.lastTestSucceeded;
          return `${formatDateTime(v, i18n.language)} · ${ok ? t('sso.testOk') : t('sso.testFailed')}`;
        },
      },
    },
    { name: 'isEnabled', label: t('sso.columns.status'), options: { customBodyRender: (v) => (v ? t('sso.enabled') : t('sso.disabled')) } },
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, meta) => <OptionsMenu actions={actions} row={rows[meta.rowIndex]} />,
      },
    },
  ], [t, i18n.language, rows, actions, statusLabels]);

  const schema = useMemo(() => getConnectionSchema(t, { connection: drawer.connection, isHolding }), [t, drawer.connection, isHolding]);

  return (
    <Container>
      {failed && <LoadErrorBanner message={t('sso.loadFailed')} />}
      <DataTable
        title={t('sso.title')}
        fetching={fetching}
        columns={columns}
        data={rows}
        serverSide={false}
        disableFilters
        canCreateNewRecord
        newButtonLabel={t('sso.new')}
        onNewButtonClick={openCreate}
        tableBodyMaxHeight="calc(100vh - 300px)"
      />
      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, connection: null })}
        title={drawer.connection ? t('sso.edit') : t('sso.new')}
        schema={schema}
        initialValues={toFormValues(drawer.connection)}
        onSave={handleSave}
      />
      <SsoEventsDrawer connection={eventsFor} onClose={() => setEventsFor(null)} />
      <ConfirmDialog
        isOpen={Boolean(deleting)}
        title={t('sso.deleteTitle')}
        message={t('sso.deleteMessage', { name: deleting?.displayName || '' })}
        onConfirm={async () => { await remove(deleting.id); setDeleting(null); }}
        onCancel={() => setDeleting(null)}
      />
    </Container>
  );
}
